// Shared tree utilities for catalogue section structures

export const updateSectionById = (sections, targetId, updater) => {
  let changed = false;
  const result = sections.map((section) => {
    if (section.id === targetId) {
      changed = true;
      return updater(section);
    }
    if (section.groups) {
      const g = updateSectionById(section.groups, targetId, updater);
      if (g !== section.groups) {
        changed = true;
        return { ...section, groups: g };
      }
    }
    return section;
  });
  return changed ? result : sections;
};

export const removeSectionById = (sections, targetId) => {
  const idx = sections.findIndex((s) => s.id === targetId);
  if (idx !== -1) return sections.filter((s) => s.id !== targetId);
  let changed = false;
  const result = sections.map((section) => {
    if (!section.groups) return section;
    const g = removeSectionById(section.groups, targetId);
    if (g === section.groups) return section;
    changed = true;
    if (g.length === 0) {
      const { groups: _removed, ...leaf } = section;
      return { ...leaf, articleIDs: [] };
    }
    return { ...section, groups: g };
  });
  return changed ? result : sections;
};

export const findSectionById = (sections, id) => {
  for (const section of sections) {
    if (section.id === id) return section;
    if (section.groups) {
      const found = findSectionById(section.groups, id);
      if (found) return found;
    }
  }
  return null;
};

export const countArticlesDeep = (section) => {
  if (section.groups && section.groups.length > 0)
    return section.groups.reduce((sum, g) => sum + countArticlesDeep(g), 0);
  return section.articleIDs?.length || 0;
};

export const collectAllArticleIDs = (sections) => {
  const ids = new Set();
  const traverse = (list) =>
    list.forEach((s) => {
      s.articleIDs?.forEach((id) => ids.add(id));
      if (s.groups) traverse(s.groups);
    });
  traverse(sections);
  return ids;
};

export const collectLeafSections = (sections, path = []) => {
  const leaves = [];
  sections.forEach((s) => {
    const currentPath = [...path, s.name];
    if (!s.groups || s.groups.length === 0)
      leaves.push({ ...s, parentPath: path, breadcrumb: currentPath.join(" → ") });
    else leaves.push(...collectLeafSections(s.groups, currentPath));
  });
  return leaves;
};

// Remove article ids from every section that references them, at any depth.
// Follows this module's convention: the original reference comes back when nothing changed, so
// callers can skip a write.
export const stripArticleIDsFromSections = (sections, idsToRemove) => {
  if (!sections) return [];
  const ids = idsToRemove instanceof Set ? idsToRemove : new Set(idsToRemove || []);
  if (ids.size === 0) return sections;

  let changed = false;
  const result = sections.map((section) => {
    let next = section;

    if (section.articleIDs) {
      const filtered = section.articleIDs.filter((id) => !ids.has(id));
      if (filtered.length !== section.articleIDs.length) {
        next = { ...next, articleIDs: filtered };
        changed = true;
      }
    }

    if (section.groups) {
      const groups = stripArticleIDsFromSections(section.groups, ids);
      if (groups !== section.groups) {
        next = { ...next, groups };
        changed = true;
      }
    }

    return next;
  });

  return changed ? result : sections;
};

// Which sections hold each article, by name — so a removal can warn about the sections the rep
// is NOT looking at. `excludeSectionId` drops the section being edited from the answer; its own
// membership is not news to the rep.
export const buildSectionNamesByArticleID = (
  sections,
  { excludeSectionId } = {}
) => {
  const map = new Map();
  const traverse = (list) =>
    (list || []).forEach((section) => {
      if (section.id !== excludeSectionId) {
        section.articleIDs?.forEach((id) => {
          const names = map.get(id);
          if (names) names.push(section.name);
          else map.set(id, [section.name]);
        });
      }
      if (section.groups) traverse(section.groups);
    });
  traverse(sections);
  return map;
};

// Strip undefined values from sections before saving to Firestore
export const sanitizeSections = (sections) =>
  sections.map((s) => {
    const clean = {};
    for (const [k, v] of Object.entries(s)) {
      if (v === undefined) continue;
      if (k === "groups" && Array.isArray(v)) {
        clean.groups = sanitizeSections(v);
      } else {
        clean[k] = v;
      }
    }
    return clean;
  });
